import React, { useEffect, useState } from "react";
import SearchBox from "./SearchBox";
import { matchesQuery } from "../utils/search";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import API_BASE from "../config";
import AlbumService from "../services/album.service";
import Spinner from "./Spinner";

// Management list page (separate from the public gallery). Staff can
// view, edit and delete albums here.
const AlbumManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [query, setQuery] = useState("");
  const filtered = list.filter((a) => matchesQuery(query, a.title, a.description));

  const resolveMsg = (err) =>
    (err.response && err.response.data && err.response.data.message) || err.message;

  const load = () => {
    setLoading(true);
    AlbumService.getAll()
      .then((res) => setList(res.data || []))
      .catch((err) => toast.error(resolveMsg(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = (album) => {
    if (!window.confirm(t("album.confirmDelete", "Delete this album and all its images?"))) return;
    setDeleting(album.id);
    AlbumService.remove(album.id)
      .then(() => {
        toast.success(t("album.deleted", "Album deleted."));
        setList((prev) => prev.filter((a) => a.id !== album.id));
      })
      .catch((err) => toast.error(resolveMsg(err)))
      .finally(() => setDeleting(null));
  };

  const cover = (album) => {
    const img = (album.images || [])[0];
    return img ? API_BASE + img.url : null;
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h3 className="mb-0">{t("album.manageTitle", "Manage Albums")}</h3>
        <div className="d-flex gap-2">
          <Link to="/gallery" className="btn btn-outline-secondary">
            {t("album.viewGallery", "View Gallery")}
          </Link>
          <Link to="/manage-albums/new" className="btn btn-primary">
            + {t("album.newAlbum", "New Album")}
          </Link>
        </div>
      </div>

      {loading ? (
        <Spinner />
      ) : list.length === 0 ? (
        <p className="text-muted">{t("album.empty", "No albums yet.")}</p>
      ) : (
        <>
        <SearchBox
          className="mb-3"
          value={query}
          onChange={setQuery}
          placeholder={t("album.searchPlaceholder", "Search albums by name...")}
        />
        {filtered.length === 0 ? (
          <p className="text-muted">{t("common.noResults", "No results match your search.")}</p>
        ) : (
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th style={{ width: 90 }}>{t("common.image")}</th>
                <th>{t("album.fieldTitle", "Title")}</th>
                <th className="text-center">{t("album.photos", "Photos")}</th>
                <th className="text-center">{t("album.status", "Status")}</th>
                <th className="text-end">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((album) => (
                <tr key={album.id}>
                  <td>
                    {cover(album) ? (
                      <img
                        src={cover(album)}
                        alt={album.title}
                        style={{ width: 72, height: 54, objectFit: "cover", borderRadius: 4 }}
                      />
                    ) : (
                      <span className="text-muted small">{t("common.noImage")}</span>
                    )}
                  </td>
                  <td>
                    <div className="fw-semibold">{album.title}</div>
                    {album.description && (
                      <div className="small text-muted text-truncate" style={{ maxWidth: 360 }}>
                        {album.description}
                      </div>
                    )}
                  </td>
                  <td className="text-center">{(album.images || []).length}</td>
                  <td className="text-center">
                    {album.published ? (
                      <span className="badge bg-success">{t("album.published", "Published")}</span>
                    ) : (
                      <span className="badge bg-secondary">{t("album.draft", "Draft")}</span>
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group btn-group-sm">
                      <Link to={`/gallery/${album.id}`} className="btn btn-outline-secondary">
                        {t("common.view", "View")}
                      </Link>
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => navigate(`/manage-albums/${album.id}`)}
                      >
                        {t("common.edit")}
                      </button>
                      <button
                        className="btn btn-outline-danger"
                        disabled={deleting === album.id}
                        onClick={() => handleDelete(album)}
                      >
                        {deleting === album.id ? "…" : t("common.delete")}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        </>
      )}
    </div>
  );
};

export default AlbumManagement;
